/**
 * Card Component
 *
 * Reusable card container component with variants and optional sections.
 * Follows design system from mockups with consistent styling.
 *
 * Usage:
 *   <Card>
 *     <Card.Header>Title</Card.Header>
 *     <Card.Body>Content</Card.Body>
 *     <Card.Footer>Actions</Card.Footer>
 *   </Card>
 */

import React from 'react';
import { theme } from '@/styles/theme';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant of the card */
  variant?: 'default' | 'outlined' | 'elevated';
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Hover effect */
  hoverable?: boolean;
  /** Clickable card */
  clickable?: boolean;
}

export const Card: React.FC<CardProps> & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
} = ({
  children,
  variant = 'default',
  padding = 'md',
  hoverable = false,
  clickable = false,
  className = '',
  ...props
}) => {
  // Base styles
  // Theme board: white card on the ground, 18px corners; separation by ground, not shadows.
  const baseStyles = `
    rounded-card
    transition-all duration-200
    ${clickable ? 'cursor-pointer' : ''}
  `;

  // Variant styles
  const variants = {
    default: `
      bg-card
      ${hoverable || clickable ? 'hover:shadow-ring-line' : ''}
    `,
    outlined: `
      bg-card shadow-ring-line
      ${hoverable || clickable ? 'hover:shadow-ring-ink' : ''}
    `,
    elevated: `
      bg-card shadow-decide
      ${hoverable || clickable ? 'hover:-translate-y-0.5' : ''}
    `,
  };

  // Padding styles
  const paddingStyles = {
    none: '',
    sm: 'p-3',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${paddingStyles[padding]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {children}
    </div>
  );
};

// Card Header subcomponent
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Divider below header */
  divider?: boolean;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  divider = false,
  className = '',
  ...props
}) => {
  const headerStyles = `
    ${divider ? 'pb-4 mb-4 border-b border-border-light dark:border-border-dark' : ''}
  `;

  return (
    <div
      className={`${headerStyles} ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
};

// Card Body subcomponent
export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardBody: React.FC<CardBodyProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};

// Card Footer subcomponent
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Divider above footer */
  divider?: boolean;
  /** Alignment of footer content */
  align?: 'left' | 'center' | 'right' | 'between';
}

const CardFooter: React.FC<CardFooterProps> = ({
  children,
  divider = false,
  align = 'right',
  className = '',
  ...props
}) => {
  const alignStyles = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  const footerStyles = `
    flex items-center gap-3
    ${divider ? 'pt-4 mt-4 border-t border-border-light dark:border-border-dark' : ''}
    ${alignStyles[align]}
  `;

  return (
    <div
      className={`${footerStyles} ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
};

// Attach subcomponents to Card
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

// Export default
export default Card;
